"use client";

import { useEffect, useState } from "react";
import { SessionProvider } from "next-auth/react";
import { usePathname } from "next/navigation";

// 公开页面，不需要认证
const PUBLIC_PAGES = ["/privacy", "/terms", "/refund-policy"];

function restoreBody(reason: string) {
  try {
    const cs = getComputedStyle(document.body);
    const overflow = document.body.style.overflow || cs.overflow;
    const pe = document.body.style.pointerEvents || cs.pointerEvents;
    if (overflow === "hidden" || pe === "none") {
      document.body.style.overflow = "auto";
      document.body.style.pointerEvents = "auto";
      document.documentElement.style.overflow = "auto";
      // eslint-disable-next-line no-console
      console.warn("[BodyGuard] restoreBody applied due to", reason, {
        overflow,
        pointerEvents: pe,
      });
    }
  } catch {}
}

export default function Providers({ children }: { children: React.ReactNode }) {
  // 确保始终返回有效的 React 元素，即使 children 为 null
  const content = children || <div>Loading application...</div>;
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);

  // 等待客户端挂载
  useEffect(() => {
    setMounted(true);
  }, []);

  // 检查是否是公开页面（更严格的检查，确保 pathname 存在）
  const isPublicPage = pathname
    ? PUBLIC_PAGES.some((page) => pathname.includes(page))
    : false;

  useEffect(() => {
    // 检查是否有打开的模态框/对话框
    const hasOpenModalLike = () =>
      !!document.querySelector('[data-state="open"]');

    restoreBody("mount");
    const obs = new MutationObserver(() => {
      // 如果有打开的对话框，不要恢复 body 样式
      if (hasOpenModalLike()) return;
      restoreBody("mutation");
    });
    obs.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });

    // 滚轮兜底：某些环境下 body/html 被外部影响导致不滚动
    const onWheelCapture = (e: WheelEvent) => {
      // 若有弹窗，尊重弹窗自身滚动逻辑
      if (hasOpenModalLike()) return;
      // 记录滚动前后，若未发生滚动，则手动滚动
      const beforeY = window.scrollY;
      // 使用微任务检查是否发生滚动
      queueMicrotask(() => {
        const afterY = window.scrollY;
        const noScroll = Math.abs(afterY - beforeY) < 1;
        if (noScroll && Math.abs(e.deltaY) > 0) {
          // 主动滚动页面
          try {
            window.scrollBy({ top: e.deltaY, behavior: "auto" });
            // 阻止默认，避免重复滚动
            e.preventDefault();
          } catch {}
        }
      });
    };

    return () => {
      obs.disconnect();
      window.removeEventListener("wheel", onWheelCapture, {
        capture: true,
      } as any);
    };
  }, [pathname]);

  // 公开页面不使用 SessionProvider
  if (isPublicPage) {
    return <>{content}</>;
  }

  return <SessionProvider>{content}</SessionProvider>;
}
